// IE Hub - Error Boundary Component
// Catches JavaScript errors anywhere in the component tree

import React, { Component, ErrorInfo } from 'react';
import { Box, Button, Container, Typography } from '@ui/index';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const CHUNK_RELOAD_SESSION_KEY = 'chunk-reload-attempted';

function isLikelyChunkLoadError(error: Error): boolean {
  const message = error?.message || '';
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('dynamic import')
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Recover from stale hashed chunks (common after deployments with aggressive caching).
    // React Router catches lazy-import failures, so the global unhandled-rejection guard may not fire.
    if (isLikelyChunkLoadError(error)) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === '1';
      } catch {
        alreadyReloaded = false;
      }

      if (!alreadyReloaded) {
        try {
          sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
        } catch {
          // ignore
        }
        window.location.reload();
        return;
      }
    }
    
    // Report to FEMA (error monitoring)
    const fema = (window as any).fema;
    if (fema && typeof fema.captureError === 'function') {
      fema.captureError(error, {
        context: 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    }
  }
  
  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/#/dashboard';
  };

  handleReportIssue = (): void => {
    // Create a bug report
    const errorReport = {
      error: this.state.error?.message || 'Unknown error',
      stack: this.state.error?.stack || 'No stack trace',
      componentStack: this.state.errorInfo?.componentStack || 'No component stack',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
    
    // Copy to clipboard
    const reportText = `IE Hub Error Report
${'-'.repeat(50)}
Error: ${errorReport.error}
URL: ${errorReport.url}
Time: ${errorReport.timestamp}

Stack Trace:
${errorReport.stack}

Component Stack:
${errorReport.componentStack}

User Agent:
${errorReport.userAgent}`;
    
    navigator.clipboard.writeText(reportText).then(() => {
      alert('Error report copied to clipboard. Please paste it in your bug report.');
    }).catch(() => {
      // Fallback: show modal with text to copy
      const textarea = document.createElement('textarea');
      textarea.value = reportText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Error report copied to clipboard. Please paste it in your bug report.');
    });
  };
  
  override render(): JSX.Element {
    if (this.state.hasError) {
      return (
        <Container data-testid="app-routes-error-boundary-container-action-1" maxWidth="md" sx={{ py: 8 }}>
          <Box data-testid="app-routes-error-boundary-container-action-2"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 3
            }}
          >
            {/* Error Icon */}
            <Box data-testid="app-routes-error-boundary-container-⚠️"
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.contrastText',
                fontSize: '2rem'
              }}
            >
              ⚠️
            </Box>
            
            {/* Error Message */}
            <Box data-testid="app-routes-error-boundary-container-action-3">
              <Typography data-testid="app-routes-error-boundary-text-oops!-something-went-wrong" variant="h4" color="error.main" gutterBottom>
                Oops! Something went wrong
              </Typography>
              <Typography data-testid="app-routes-error-boundary-text-action-1" variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                We&apos;re sorry, but an unexpected error occurred. The development team has been notified.
              </Typography>
              {(import.meta as any).env?.DEV && this.state.error && (
                <Box data-testid="app-routes-error-boundary-container-action-4"
                  sx={{
                    textAlign: 'left',
                    backgroundColor: 'grey.100',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: 200,
                    mt: 2
                  }}
                >
                  <Typography data-testid="app-routes-error-boundary-text-error-details-(development-mode):" variant="subtitle2" color="error.main" gutterBottom>
                    Error Details (Development Mode):
                  </Typography>
                  <Typography data-testid="app-routes-error-boundary-text-action-2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 'inherit' }}>
                    {this.state.error.message}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Action Buttons */}
            <Box data-testid="app-routes-error-boundary-container-action-5" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button data-testid="app-routes-error-boundary-btn-reload-page"
                variant="contained"
                color="primary"
                onClick={this.handleReload}
                size="large"
              >
                Reload Page
              </Button>
              <Button data-testid="app-routes-error-boundary-btn-go-to-dashboard"
                variant="outlined"
                onClick={this.handleGoHome}
                size="large"
              >
                Go to Dashboard
              </Button>
              <Button data-testid="app-routes-error-boundary-btn-report-issue"
                variant="text"
                onClick={this.handleReportIssue}
                size="large"
                color="inherit"
              >
                Report Issue
              </Button>
            </Box>
            
            {/* Additional Help */}
            <Box data-testid="app-routes-error-boundary-container-action-6" sx={{ mt: 2 }}>
              <Typography data-testid="app-routes-error-boundary-text-action-3" variant="body2" color="text.secondary">
                If this problem persists, please contact support at{' '}
                <a data-testid="app-routes-error-boundary-link-support@iehub.com" href="mailto:support@iehub.com" style={{ color: 'inherit' }}>
                  support@iehub.com
                </a>
              </Typography>
            </Box>
          </Box>
        </Container>
      );
    }
    
    return this.props.children as JSX.Element;
  }
}

export default ErrorBoundary;
